import { SORTED_COUNTRIES } from '@modules/settings/my-numbers/purchase-number/constants/purchase-number.constant';
import { Input, InputRef } from 'antd';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PopoverCountriesListProps } from './phone-input.types';
import { useTranslation } from 'react-i18next';

export const PopoverCountriesList = ({
  countryCode,
  setDialingCode,
  closePopover,
  isPopoverOpen,
  bordered,
}: PopoverCountriesListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputRef>(null);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();
  const lowerCaseSearchText = searchText.trim().toLowerCase();

  const filteredCountries = useMemo(
    () =>
      lowerCaseSearchText
        ? SORTED_COUNTRIES.filter(
            (country) =>
              country.name.toLowerCase().startsWith(lowerCaseSearchText) ||
              country.alphaTwoCode.toLowerCase().startsWith(lowerCaseSearchText)
          )
        : SORTED_COUNTRIES,
    [lowerCaseSearchText]
  );

  const countryItems = useMemo(() => {
    return filteredCountries.map(({ name, flagUrl, alphaTwoCode, dialingCode }) => {
      const isNP = alphaTwoCode === 'NP';
      return (
        <button
          key={alphaTwoCode}
          type="button"
          data-country={alphaTwoCode}
          className={clsx(
            'flex gap-2 items-center px-3 py-2 w-full text-left cursor-pointer rounded',
            'focus:outline-primary-300! focus:outline',
            countryCode?.toUpperCase() === alphaTwoCode ? 'bg-primary-100' : 'hover:bg-gray-200 focus:bg-gray-200'
          )}
          onClick={() => {
            setDialingCode(dialingCode, alphaTwoCode);
            closePopover();
          }}
          // handle up and down arrow keys to navigate through the countries
          onKeyDown={(event) => {
            const buttons = Array.from(
              listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-country]') || []
            );

            const currentIndex = buttons.findIndex((btn) => btn.dataset.country === alphaTwoCode);

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              const nextIndex = (currentIndex + 1) % buttons.length;
              buttons[nextIndex].focus();
              buttons[nextIndex].scrollIntoView({ block: 'nearest' });
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              const prevIndex = (currentIndex - 1 + buttons.length) % buttons.length;
              buttons[prevIndex].focus();
              buttons[prevIndex].scrollIntoView({ block: 'nearest' });
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              buttons[currentIndex]?.click();
            }

            if (event.key === 'Escape') {
              closePopover();
            }
          }}
        >
          <div className="shrink-0 size-6 grid place-items-center">
            <img
              src={flagUrl}
              className={clsx(
                isNP && 'size-5!',
                !isNP && !bordered && 'rounded-full',
                bordered ? 'w-[27px] h-5' : 'size-6'
              )}
            />
          </div>
          <span className="text-sm">{name}</span>
        </button>
      );
    });
  }, [filteredCountries, countryCode]);

  useEffect(() => {
    if (!isPopoverOpen || !listRef.current) return;

    // this runs after browser paints the DOM
    const raf = requestAnimationFrame(() => {
      const selected = listRef.current?.querySelector(
        `[data-country="${countryCode?.toUpperCase()}"]`
      ) as HTMLButtonElement | null;

      selected?.scrollIntoView({ block: 'nearest' });
      inputRef.current?.focus();
    });

    return () => cancelAnimationFrame(raf);
  }, [countryCode, isPopoverOpen]);

  return (
    <div className="">
      <div className="py-2 px-3">
        <Input
          ref={inputRef}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search country"
        />
      </div>
      <div ref={listRef} className="max-h-52 w-full overflow-auto px-2 pb-2 pt-1">
        {countryItems}
        {filteredCountries.length === 0 && <div className="px-2 py-3 text-sm">{t('no_countries_found')}</div>}
      </div>
    </div>
  );
};
